"use client";

import * as React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
} from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartConfig,
} from "@/components/ui/chart";
import { AppConfig } from "@/utils/AppConfig";
import { useTranslation } from "react-i18next";

type AnalyticsItem = {
  nameAR: string;
  nameEN: string;
  productCost: number | string;
  price: number | string;
};

interface CostChartProps {
  data: AnalyticsItem[] | null;
}

export function CoastChart({ data }: CostChartProps) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;

  const chartData = React.useMemo(() => {
    return (
      data?.map((item) => ({
        name: lang === "ar" ? item.nameAR : item.nameEN,
        cost: Number(item.productCost),
        selling: Number(item.price),
      })) || []
    );
  }, [data, lang]);

  const chartConfig = {
    cost: {
      label: t("CoastChart.Cost"),
      color: "#ef4444",
    },
    selling: {
      label: t("CoastChart.Price"),
      color: "#10b981",
    },
  } satisfies ChartConfig;

  // 📐 Width based on number of bars: 80px per item
  const chartWidth = Math.max(500, chartData.length * 125);

  return (
    <Card className="force-light-mode bg-white text-black border rounded-lg shadow-lg">
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-gray-800">
          {t("CoastChart.CostvsSellingChart")}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <ChartContainer
          config={chartConfig}
          className="h-[500px] w-full bg-gray-100 rounded-lg py-6 shadow-inner"
        >
          {/* ✅ Scroll wrapper */}
          <div className="w-full overflow-x-auto">
            <div style={{ width: chartWidth }}>
              <BarChart
                data={chartData}
                width={chartWidth}
                height={500}
                margin={{ top: 8, right: 12, left: 24, bottom: 80 }}
              >
                <CartesianGrid vertical={false} horizontal={false} />

                <XAxis
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  interval={0}
                  angle={lang === "ar" ? 90 : -90}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12, fill: "#4A5568" }}
                  tickFormatter={(value: string) =>
                    value.length > 10 ? value.slice(0, 10) + "..." : value
                  }
                />

                <ChartTooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) return null;

                    return (
                      <div className="bg-black text-white p-3 rounded-lg w-[180px]">
                        <div className="mb-1 font-semibold">{label}</div>
                        {payload.map((entry, idx) => {
                          const key = entry.dataKey as "cost" | "selling";
                          const translatedLabel = chartConfig[key]?.label;
                          const color = chartConfig[key]?.color;

                          return (
                            <div key={idx} className="flex justify-between text-sm items-center">
                              <span className="flex items-center gap-2">
                                <span
                                  className="inline-block rounded-sm w-3 h-3"
                                  style={{ backgroundColor: color }}
                                ></span>
                                {translatedLabel}
                              </span>
                              <span className="flex items-center gap-1">
                                {entry.value}
                                {AppConfig.currency.icon("white", 12)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }}
                />

                <Bar dataKey="cost" fill="var(--color-cost)" radius={4} />
                <Bar dataKey="selling" fill="var(--color-selling)" radius={4} />
              </BarChart>
            </div>
          </div>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
